import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Button,
  Container,
  Divider,
  Group,
  List,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import {
  IconDatabase,
  IconKey,
  IconSparkles,
  IconUsersGroup,
  type Icon,
} from '@tabler/icons-react';

import { useMe } from '../auth/api';
import { visibleReportGroups } from '../raporty/nav';

interface Tile {
  to: string;
  label: string;
  icon: Icon;
}

// Bump gdy dojdzie coś nowego — wtedy modal „Co nowego" pokaże się ponownie.
// Konwencja: przy każdej zmianie dopisz punkt do najnowszej sekcji (albo dodaj nową
// sekcję z datą) i ustaw tę stałą na tę datę.
const WHATS_NEW_VERSION = '2026-11-02';

const WHATS_NEW: { date: string; groups: { area: string; items: string[] }[] }[] = [
  {
    date: '2 listopada 2026',
    groups: [
      {
        area: 'Oprogramowanie',
        items: [
          'Nowy przycisk „Wystaw ZO" obok „Przygotuj maile": wystawia w Enovie zamówienia od odbiorcy za godziny wdrożeniowe — po jednym na klienta, z jedną pozycją „Usługi wdrożeniowe". Widzą go tylko osoby z uprawnieniem do wystawiania ZO.',
          'Okno pokazuje wszystkich klientów z godzinami w wybranym okresie, z checkboxami. Przy każdym widać czas, ilość godzin na ZO (minuty / 60, z dokładnością do 0,01 h), stawkę, wartość netto, kontrahenta i ostatnie ZO tego klienta. Klientów bez konfiguracji ZO nie da się zaznaczyć — można ją uzupełnić od razu z okna.',
          'Okres bierze się z zakresu dat raportu: data końca trafia do opisu jako „rozliczenie do DD.MM.RRRR", a datą ZO jest dzień wystawienia. Okno ostrzega, gdy okres nachodzi na wcześniejsze ZO klienta albo zostawia lukę, i nie pozwala wystawić drugi raz ZO za ten sam okres.',
          'ZO liczy wszystkie godziny projektu — filtry osób i statusów są przy nim pomijane.',
          'Dokumenty powstają w buforze: przejrzyj je i zatwierdź w Enovie. Gdy Enova nie odpowie, okno pokazuje „wynik nieznany" i przycisk „Sprawdź ponownie" — ponowienie nie utworzy duplikatu.',
        ],
      },
    ],
  },
  {
    date: '8 września 2026',
    groups: [
      {
        area: 'Projekty w czasie',
        items: [
          'Nad wykresem jest przełącznik osi czasu: miesiąc, kwartał albo rok. Miesiąc to najniższe ziarno danych, więc niżej nie da się zejść.',
          'Klik w słupek zawęża listę dokumentów pod wykresem do tego okresu. Wybrany okres jest zaznaczony pionową linią na wykresie i podświetlonym wierszem w tabeli, a nad listą pojawia się plakietka „tylko <okres>" — krzyżyk albo drugi klik w ten sam słupek zdejmuje zawężenie. To samo robi klik w okres w tabeli pod wykresem.',
          'Kliknięcie w słupek, który powstał z samych godzin (praca przed fakturą), daje pustą listę dokumentów — to nie błąd, w tym miesiącu po prostu nie ma jeszcze dokumentów.',
          'Klik w numer dokumentu otwiera jego szczegóły: numer i data dokumentu, ewidencja, data wpływu, projekt, kontrahent, prowadzący oraz wszystkie pozycje budżetów, na które ten dokument wszedł. Jeśli dokument dotyczy kilku projektów, okno mówi to wprost — suma jest wtedy sumą całego dokumentu, a nie wiersza, z którego się w nie weszło.',
          'W szczegółach jest link do dokumentu w Enovie. Pokazuje się dopiero po ustawieniu adresu w konfiguracji — do tego czasu widać sam numer.',
          'Uwaga: szczegóły pokazują rozbicie dokumentu na pozycje budżetów projektów, a nie pozycje faktury. Towarów, ilości i cen jednostkowych nie ma w danych, na których stoi ten raport.',
          'Zapisany widok tego ekranu pamięta też wybraną granulację osi. Widoki zapisane wcześniej wczytują się na miesiącach.',
        ],
      },
    ],
  },
  {
    date: '4 września 2026',
    groups: [
      {
        area: 'Projekty — wykresy',
        items: [
          'Wykres na „Projektach w czasie" pokazuje przychody i koszty jako kolumny, a wynik jako linię — z podpowiedzią po najechaniu (kwoty za dany miesiąc) i klikalną legendą.',
          'Na „Podsumowaniu" są dwa wykresy kołowe: przychody i koszty w podziale na obszary, z sumą w środku. Pod nimi tabela z wynikiem i udziałem każdego obszaru.',
        ],
      },
    ],
  },
  {
    date: '3 września 2026',
    groups: [
      {
        area: 'Projekty (nowy raport)',
        items: [
          'W menu jest nowy raport „Projekty — godziny i koszt pracy". Pokazuje, ile godzin i za jaki koszt poszło na który projekt, z rozbiciem na osoby: rozwijasz pracownika, pod nim obszar, podobszar i konkretne projekty. Dane są te same, na których stoi dotychczasowy raport w Power BI.',
          'Klik w nazwę projektu otwiera listę pojedynczych wpisów czasu — kto, jakie zadanie, od której do której godziny. Kod zadania jest linkiem do zgłoszenia w WAN.',
          'Filtry: pracownik, obszar, podobszar i zakres dat wpisu. Pusty filtr znaczy „bez zawężenia", więc na wejściu widać całość.',
          'Godziny osoby, która nie ma ustawionej stawki, mają w kolumnie stawki znacznik „brak stawki" — taka praca liczy się do projektu za darmo, więc koszt jest wtedy zaniżony. W Power BI nie było tego widać.',
        ],
      },
      {
        area: 'Wynik projektów (nowy ekran)',
        items: [
          'W grupie Projekty jest drugi ekran, „Wynik projektów": przychody, koszty dokumentów i koszt pracy per projekt, plus wynik. Projekty dopłacające są na górze i mają wynik na czerwono.',
          'Filtry: stan projektu (aktywny), prowadzący, kontrahent, obszar, podobszar i zakres miesięcy. Kontrahent filtruje po identyfikatorze z Enovy, nie po nazwie, bo ta sama firma bywa tam wpisana pod kilkoma nazwami.',
          'Wynik liczy się jako przychody minus koszty dokumentów minus koszt pracy. Uwaga na godziny osób bez ustawionej stawki: wchodzą jako zero, więc zawyżają wynik.',
        ],
      },
      {
        area: 'Pracownicy na kontrahentach (nowy ekran)',
        items: [
          'Trzeci ekran w grupie Projekty: godziny i koszt pracy w podziale na kontrahentów, rozwijane po osobach, posortowane od największego kosztu.',
          'Jest tam grupa „bez kontrahenta" — godziny na projektach bez przypisanego kontrahenta oraz wpisy bez projektu. W Power BI takie godziny wypadały ze zestawienia po cichu.',
        ],
      },
      {
        area: 'Udział pracowników w wyniku (nowy ekran)',
        items: [
          'Czwarty ekran w grupie Projekty: budżet projektu rozdzielony proporcją godzin, per osoba i miesiąc. Przełącznik pokazuje albo procent wiersza, albo kwoty.',
          'To nie jest wynik projektu i nie sumuje się z ekranem „Wynik projektów" — nie ma tu przychodu z dokumentów ani kosztu pracy, tylko podział budżetu. Ostrzeżenie o tym jest na ekranie.',
          'Nad tabelą widać, ile projektów ma godziny, ale nie ma budżetu miesięcznego. Takie projekty nie wchodzą na ten ekran w ogóle, razem z ich godzinami — w Power BI nie było o tym śladu.',
        ],
      },
      {
        area: 'Projekty w czasie (nowy ekran)',
        items: [
          'Piąty ekran w grupie Projekty: wykres przychodów, kosztów i wyniku miesiąc po miesiącu, pod nim te same liczby w tabeli.',
          'Niżej lista dokumentów, z których te kwoty pochodzą: data wpływu, numer dokumentu i ewidencji, projekt, pozycja budżetu i kwota ze znakiem (koszty na minus).',
        ],
      },
      {
        area: 'Podsumowanie projektów (nowy ekran)',
        items: [
          'Szósty i ostatni ekran w grupie Projekty, wejście do raportu: wynik w podziale na obszary z paskiem udziału, plus sumy całości.',
          'Pod spodem sekcja „Stan danych źródłowych": ile godzin zaraportowały osoby bez ustawionej stawki (taka praca wchodzi do projektów za darmo i zawyża wynik) oraz ile zadań nie ma kodu WAN. Lista osób bez stawki jest imienna.',
          'Tym samym w DZiK-u są wszystkie sześć stron dotychczasowego raportu Power BI „Projekty".',
        ],
      },
      {
        area: 'Nawigacja',
        items: [
          'Raporty w menu bocznym są podzielone na grupy: Sprzedaż, CRM, Dane, Księgowość, Wdrożenia i Projekty. Wcześniej wszystkie wisiały na jednej liście „Raporty". Ten sam podział jest na pulpicie.',
          'Grupy w menu są domyślnie zwinięte. Rozwija się sama tylko ta, w której właśnie jesteś.',
          'Rachunek Zysków i Strat pojawił się wreszcie na pulpicie. Do tej pory był tylko w menu bocznym.',
          'Nazwy raportów bez myślników: „Raport 1 (sprzedaż zbiorcza)" zamiast „Raport 1 — Sprzedaż zbiorcza".',
        ],
      },
    ],
  },
  {
    date: '2 września 2026',
    groups: [
      {
        area: 'Raport 2',
        items: [
          'Kolumna „Pozycja Ceneo" w sekcji Ceneo (API) nazywa się teraz „Pozycja cenowa" i pokazuje, ilu sklepów jest realnie tańszych (1 = jesteśmy najtańsi). Wcześniej była to surowa pozycja z Ceneo, w której stoją też oferty promowane — sklep mógł być nad nami, nie będąc tańszym.',
          'Dwa sklepy w tej samej cenie mają tę samą pozycję. Surowy ranking Ceneo widać dalej w liście ofert, w kolumnie „Poz. Ceneo" — różnica między nią a pozycją cenową pokazuje, kto jest promowany.',
          'Lista ofert pod „Pozycją cenową" jest teraz uporządkowana od najtańszej oferty, a nie po pozycji w rankingu Ceneo — od razu widać, kto schodzi poniżej nas.',
          'W nagłówku tej listy jest link „Ceneo", który otwiera kartę produktu na Ceneo w nowym oknie. Dla PN-ów, dla których Ceneo nie podało nam jeszcze identyfikatora karty, link prowadzi do wyszukiwania po PN.',
        ],
      },
    ],
  },
  {
    date: '25 sierpnia 2026',
    groups: [
      {
        area: 'Raport 2',
        items: [
          'Sekcja „Ceneo (Monopolex)" nazywa się teraz „Ceneo (API)" i bierze dane wprost z Ceneo, przez nasz serwis Ceneo API. Kolumny bez zmian: pozycja, nasza cena, najniższa cena, liczba ofert.',
          'Ceneo udostępnia tylko dziesięć najlepszych ofert, więc „Liczba ofert" oznacza liczbę ofert w TOP 10, a nie wszystkie oferty produktu. Jeśli nasza oferta jest poza dziesiątką, w kolumnie „Pozycja Ceneo" widnieje „poza TOP 10".',
          'Dane odświeżają się raz na dobę — tyle, ile wynosi zaciąg po stronie Ceneo. Sekcja „Ceneo (ręczne)", sortowanie i marża działają jak dotąd, na pliku wgrywanym w R2.',
        ],
      },
    ],
  },
  {
    date: '23 sierpnia 2026',
    groups: [
      {
        area: 'Oprogramowanie',
        items: [
          'Okres rozliczeniowy liczy się jako tydzień od piątku do czwartku. Przyciski „Poprzedni okres rozliczeniowy" i „Bieżący okres rozliczeniowy" ustawiają dokładnie taki przedział — nie tydzień kalendarzowy i nie miesiąc.',
          'Doszły przyciski „Poprzedni miesiąc" i „Bieżący miesiąc", ustawiające cały miesiąc kalendarzowy — dla klientów rozliczanych miesięcznie.',
          'Presety ustawiają zawsze pełny okres, także gdy jego ostatni dzień jeszcze nie minął: to jego daty są nazwą pliku na SharePoincie, więc zakres ucięty do dziś tworzyłby obok drugi plik zamiast nadpisania tego samego.',
        ],
      },
    ],
  },
  {
    date: '21 sierpnia 2026',
    groups: [
      {
        area: 'Oprogramowanie',
        items: [
          'Nowy raport „Oprogramowanie — godziny wdrożeniowe" (menu Raporty): godziny zadań wdrożeniowych rozliczane z klientami, z filtrami po kliencie, osobie wykonującej, statusie i zakresie dat.',
          'Kilku klientów naraz — jeden przebieg daje osobne zestawienie dla każdego z nich, z własną sumą godzin i sumą łączną na górze ekranu.',
          'Przyciski szybkiego zakresu ustawiają okres rozliczeniowy jednym kliknięciem.',
          'Trzy tryby prezentacji: płasko (wszystkie wpisy), sumy per kod zadania i sumy per osoba — bez ręcznego sumowania w Excelu.',
          'Czas pokazywany jest jako HH:MM (62:30, nie „2 days, 14:30:00"), również w sumach i w eksporcie.',
          'Eksport: „Eksport XLSX" pobiera arkusz wybranego klienta na dysk. „Zapisz na SharePoint" odkłada rozliczenia do biblioteki Wdrożenia (Raporty/<kod projektu>/<okres>.xlsx) — osobny plik na klienta, żeby arkusz nie zawierał cudzych danych. Przed zapisem widzisz docelowe ścieżki; plik z tym samym okresem zostaje nadpisany, a poprzednia wersja zostaje w historii wersji SharePointa.',
          '„Przygotuj maile" tworzy w Twojej skrzynce wersje robocze maili — po jednej na klienta, z odbiorcami, tematem, treścią i załączonym arkuszem. Przed utworzeniem widzisz podgląd, a maile wysyłasz sam z Outlooka: aplikacja nie wysyła niczego.',
          'Zapisane widoki — jak w pozostałych raportach: zestaw filtrów można zapisać pod nazwą, udostępnić i ustawić jako domyślny.',
        ],
      },
      {
        area: 'Pulpit',
        items: [
          'Kafelki na pulpicie pokazują już wszystkie dostępne raporty — doszła „Aktywność CRM", „Historia dystrybucji" i „Oprogramowanie".',
        ],
      },
    ],
  },
  {
    date: '20 sierpnia 2026',
    groups: [
      {
        area: 'Aktywność CRM',
        items: [
          'Nowy raport „Aktywność CRM" — liczba spotkań, telefonów, maili i notatek handlowców z enova365, z filtrami po handlowcu, rodzaju aktywności i zakresie dat.',
          'Cztery widoki: podsumowanie wg handlowca i miesiąca, pełna lista aktywności z opisami, rozbicie na kontrahentów oraz metodyka wyjaśniająca, skąd biorą się liczby.',
          'Eksport całości do XLSX — cztery arkusze odpowiadające zakładkom, z informacją kto i kiedy wygenerował plik.',
          'Uwaga do liczb: rodzaj aktywności nie jest w enovie osobnym polem, tylko ręcznie wpisaną nazwą zadania. Nazwy, których raport nie rozpoznaje, są oznaczone i wypisane na górze ekranu.',
          'Zapisane widoki — jak w Raporcie 1 i 2: zestaw filtrów można zapisać pod nazwą, udostępnić innym i ustawić jako domyślny, wczytywany przy wejściu.',
          'Raport wymaga wskazania handlowca — nie ma trybu zbiorczego dla całego działu, bo lista aktywności zawiera pełne notatki z rozmów.',
        ],
      },
    ],
  },
  {
    date: '29 lipca 2026',
    groups: [
      {
        area: 'Raport 1',
        items: [
          'Przy każdej tabeli — dwa wskaźniki „na teraz": „Stan" (wartość aktualnego stanu) i „Zalegacze" (wartość towaru starszego niż 4 mies.), liczone z magazynów Firma / Firma paletowy dla wybranych cech bloku.',
          'Klik w „Stan" lub „Zalegacze" pokazuje partie: PN, magazyn, data przyjęcia, ilość, cena jednostkowa partii i wartość. Uwaga: po inwenturze data przyjęcia może być datą inwentury.',
        ],
      },
    ],
  },
  {
    date: '23 lipca 2026',
    groups: [
      {
        area: 'Raport 2',
        items: [
          'Nowa sekcja „Ceneo (Monopolex)" — automatycznie synchronizowane oferty Ceneo (bez wgrywania pliku): nasza pozycja, nasza cena, najniższa cena i liczba ofert per PN. Działa obok dotychczasowej sekcji „Ceneo (ręczne)".',
          'Drill „Pozycja Ceneo" — klik w pozycję pokazuje pełną listę ofert: sklep, cena, liczba opinii; nasza oferta wyróżniona. Po najechaniu na pozycję — ile brakuje do najniższej ceny.',
        ],
      },
    ],
  },
  {
    date: '21 lipca 2026',
    groups: [
      {
        area: 'Raport 2',
        items: [
          'Drill „Ilość ZO" — klik w liczbę pokazuje listę zamówień odbiorcy: numer ZO, datę, kontrahenta, ilość niezrealizowaną oraz status płatności powiązanej faktury (Rozliczona / Częściowo / Nierozliczona / Brak faktury) z terminem.',
          'Drill partii (klik w średnią cenę zakupu) — nowa kolumna „Dostawca": od kogo dana partia została kupiona.',
          'Nowa kolumna „Dokumenty WZ" — ile dokumentów WZ wystawiono dla danego PN w wybranym okresie (sortowalna, w eksporcie CSV też).',
          'Drill sztuk/dokumentów (klik w Sztuki lub Dokumenty WZ) rozbudowany: numer ZO, operator (handlowiec z powiązanej faktury) i podsumowanie liczby dokumentów.',
        ],
      },
    ],
  },
  {
    date: '17 lipca 2026',
    groups: [
      {
        area: 'Raport 2',
        items: [
          'Zapisane widoki — zapisz własny zestaw filtrów/okresu/sekcji, opcjonalnie udostępnij wszystkim (★).',
          'Rezerwacje (dyst.) — nowa, ręcznie edytowalna sekcja: numer rezerwacji, ilość i cena per PN (klik w komórkę → dodaj/usuń).',
          'Najniższa cena zakupu w wybranym okresie (z przyjęć PZ) — kolumna w sekcji Enova.',
          'Najniższa cena dystrybucji w okresie — aplikacja zbiera historię cen z feedu (od teraz) i pokazuje minimum z miesiąca/kwartału/roku.',
          'Filtr wielu PN naraz — wklej listę part numberów; nieznalezione podświetlą się na czerwono.',
          'Przełączniki „stan / obrót / zalegające" rozbite na trzy niezależne (można dowolnie łączyć).',
          'Sekcję Enova można teraz ukryć (jak Koval/Ceneo/Marża/Dystrybucja).',
          'Podczas przeliczania tabela się wyszarza i blokuje edycję/paginację (nie zmienisz danych w trakcie).',
          'Domyślnie widać wszystkich producentów (nie tylko Dell).',
          'Eksport CSV pobiera wszystkie przefiltrowane wiersze (nie tylko bieżącą stronę).',
          'Węższe kolumny PN i Nazwa (Nazwa obcinana, pełna w dymku).',
        ],
      },
      {
        area: 'Raport 1',
        items: [
          'Nowa metryka „Marża %" (marża / obrót w danym okresie, ważona obrotem).',
          'Wydajność — szybkie przełączanie metryk/granulacji (zwłaszcza tydzień) nie zawiesza już przeglądarki.',
          'Domyślny widok per użytkownik — sam ładuje się po wejściu (przycisk „Ustaw jako domyślny").',
          'W widoku tygodniowym pod każdym rokiem widać datę początku tygodnia.',
          'Raport rozciąga się na pełną szerokość ekranu.',
          'Korekty WZ (KWZ) wykluczone z obrotu.',
        ],
      },
      {
        area: 'Pozostałe',
        items: [
          'Dane odświeżają się co 30 min; na R1 doszedł przycisk ręcznego odświeżenia i znacznik „Dane z:".',
          'Ekran startowy to kokpit z kafelkami (zamiast changelogu).',
        ],
      },
    ],
  },
];

function Section({ title, tiles }: { title: string; tiles: Tile[] }) {
  if (tiles.length === 0) {
    return null;
  }
  return (
    <Stack gap="sm">
      <Text fw={600} c="dimmed" tt="uppercase" size="sm">
        {title}
      </Text>
      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
        {tiles.map(({ to, label, icon: TileIcon }) => (
          <Paper
            key={to}
            component={Link}
            to={to}
            withBorder
            radius="md"
            p="lg"
            style={{ textDecoration: 'none' }}
          >
            <Stack gap="xs" align="flex-start">
              <TileIcon size={28} />
              <Text fw={600}>{label}</Text>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
}

function Home() {
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const allowed = new Set(me.data?.allowed_reports ?? []);

  const [seenVersion, setSeenVersion] = useLocalStorage<string>({
    key: 'dzik:whats-new-seen',
    defaultValue: '',
    getInitialValueInEffect: false,
  });
  const [whatsNew, setWhatsNew] = useState(false);
  // Pokaż automatycznie przy wejściu, dopóki użytkownik nie potwierdzi bieżącej wersji.
  useEffect(() => {
    if (seenVersion !== WHATS_NEW_VERSION) {
      setWhatsNew(true);
    }
  }, [seenVersion]);

  const dismissWhatsNew = () => {
    setSeenVersion(WHATS_NEW_VERSION);
    setWhatsNew(false);
  };

  // Raporty i ich podział na grupy: `raporty/nav.ts`, wspólny z menu bocznym.
  // Wcześniej stała tu druga, niezależna lista i rozjeżdżała się z menu.
  const groups = visibleReportGroups(isAdmin, allowed);

  const administracja: Tile[] = isAdmin
    ? [
        { to: '/administracja/uzytkownicy', label: 'Użytkownicy', icon: IconUsersGroup },
        { to: '/administracja/role', label: 'Role', icon: IconKey },
        { to: '/administracja/zrodla-danych', label: 'Źródła danych', icon: IconDatabase },
      ]
    : [];

  return (
    <Container size="lg" py="md">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Title order={1}>DZiK</Title>
          <Button
            variant="light"
            leftSection={<IconSparkles size={16} />}
            onClick={() => setWhatsNew(true)}
          >
            Zobacz co nowego
          </Button>
        </Group>
        {groups.map((group) => (
          <Section key={group.title} title={group.title} tiles={group.reports} />
        ))}
        <Section title="Administracja" tiles={administracja} />
      </Stack>

      <Modal
        opened={whatsNew}
        onClose={dismissWhatsNew}
        title={
          <Group gap="xs">
            <IconSparkles size={20} />
            <Text fw={700} size="lg">
              Co nowego
            </Text>
          </Group>
        }
        size="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Zmiany są wdrożone i w trakcie testów — daj znać, jeśli coś nie działa jak trzeba.
          </Text>
          {WHATS_NEW.map((release, i) => (
            <Stack key={release.date} gap="sm">
              {i > 0 && <Divider />}
              <Text fw={700} tt="uppercase" c="dimmed" size="xs">
                {release.date}
              </Text>
              {release.groups.map((group) => (
                <Stack key={group.area} gap="xs">
                  <Text fw={600}>{group.area}</Text>
                  <List spacing={6} size="sm">
                    {group.items.map((item) => (
                      <List.Item key={item}>{item}</List.Item>
                    ))}
                  </List>
                </Stack>
              ))}
            </Stack>
          ))}
          <Group justify="flex-end">
            <Button onClick={dismissWhatsNew}>OK, rozumiem</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export const Route = createFileRoute('/')({
  component: Home,
});
